import React from 'react'
// libs
import styled from 'styled-components'
import { reduxForm } from 'redux-form'
import { useMutation } from '@apollo/client/react'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxEntitySelect, ReduxInput, ReduxSelect } from 'components/FormElements/ReduxForm'
// context
import { UsersPageContext } from 'containers/Users/layers/API'
// hooks
import { useCurrentUser } from 'hooks/index'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// constants
import { Forms } from 'constants/forms'
import { AUTH_METHOD_OPTIONS } from 'constants/users'
// graphql
import { USER_CREATE_MUTATION } from 'graphql/user/mutations'
import { COMPANIES_SELECT_QUERY } from 'graphql/company/queries'
import { BUSINESS_UNITS_SELECT_QUERY } from 'graphql/businessUnit/queries'
// themes
import { colors } from 'themes/index'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { businessUnitProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'
import { getValueFromOption } from 'utils/index'
import { email, password, required } from 'utils/validators'

const ButtonsContainer = styled('div')`
  background: ${colors.gray200};
  height: 60px;
  padding: 10px 20px;
  display: flex;
  gap: 16px;
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
`

const Form = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  > div:last-child {
    margin-top: auto;
  }
`

const getCompanyOptions = ({ data }) =>
  get(data, 'companies.data', []).map(({ id, name }) => ({
    value: id,
    label: name
  }))

const handleCompanySearch = ({ search, refetch }) => refetch({ filter: { name: search } })

interface Props {
  hideModal: () => void
}

const CreateUserForm: React.FC<InjectedFormProps<any, Props> & Props> = ({ valid, hideModal, handleSubmit }) => {
  const user = useCurrentUser()

  const [userCreateMutation] = useMutation(USER_CREATE_MUTATION)

  const { refetch } = React.useContext(UsersPageContext)

  const handleFormSubmit = async (data) => {
    try {
      await userCreateMutation({
        variables: {
          user: {
            username: data.username,
            password: data.password,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            businessUnitIds: get(data, 'businessUnits', []).map(({ value }) => value),
            authMethod: getValueFromOption(data.authMethod),
            companyId: get(data, 'company.value', user.companyId)
          }
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'CreateUserForm.handleFormSubmit')
    }
  }

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      {user.isAdmin && (
        <ReduxEntitySelect
          query={COMPANIES_SELECT_QUERY}
          label={'Account'}
          name={'company'}
          getOptions={getCompanyOptions}
          handleSearch={handleCompanySearch}
        />
      )}
      <ReduxInput name={'firstName'} label={'First Name'} validate={[required]} />
      <ReduxInput name={'lastName'} label={'Surname'} validate={[required]} />
      <ReduxInput name={'email'} label={'Email'} type={'email'} validate={[required, email]} />
      <ReduxEntitySelect
        query={BUSINESS_UNITS_SELECT_QUERY}
        label={'Business Unit'}
        name={'businessUnits'}
        isMultiple
        {...businessUnitProps}
      />
      <ReduxSelect
        name={'authMethod'}
        label={'Authentication Method'}
        options={AUTH_METHOD_OPTIONS}
        validate={[required]}
      />
      <ReduxInput name={'username'} label={'Username'} validate={[required]} />
      <ReduxInput name={'password'} label={'Password'} type={'password'} validate={[required, password]} />
      <ButtonsContainer>
        <Button type={'submit'} layout={BUTTON_LAYOUTS.PURPLE} disabled={!valid}>
          Create
        </Button>
        <Button type={'button'} layout={BUTTON_LAYOUTS.WHITE} onClick={hideModal}>
          Cancel
        </Button>
      </ButtonsContainer>
    </Form>
  )
}

export default reduxForm<any, Props>({
  form: Forms.NEW_USER_FORM
})(CreateUserForm)
